import {map, takeUntil, tap} from 'rxjs/operators';
import {Component, ViewChild, OnDestroy, OnInit} from '@angular/core';
import {NotificationsService} from '@modules/notification/core/notifications.service';
import {fuseAnimations} from '@fuse/animations';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {CommunicationCenterService} from '@modules/communication-center';
import {DataEntity} from 'octopus-connect';
import {NotificationAction, NotificationInterface} from '@modules/notification/core/notification.interface';
import {NotificationDefinition} from '@modules/notification';
import {Subject} from 'rxjs';
import {Router} from '@angular/router';
import {NotificationsServiceCitizen} from '@modules/notification/core/notifications.service.citizen';

interface NotificationGroup {
    date: string;
    notifications: NotificationInterface[];
}

interface NotificationStatusGroup {
    status: NotificationStatus;
    dateGroups: NotificationGroup[];
}

export enum NotificationStatus {
    toSee = 'notification.to_see',
    alreadySeen = 'notification.already_seen',
}

@Component({
    selector: 'app-notif-list',
    templateUrl: './notif-list.component.html',
    styleUrls: ['./notif-list.component.scss'],
    animations: fuseAnimations,
    standalone: false
})
export class NotifListComponent implements OnDestroy, OnInit {

    // Remplacement de Object par Record<string, unknown> pour respecter les règles ESLint
    private notifs: Record<string, unknown>;
    public dataSource = new MatTableDataSource<NotificationInterface>();
    public notifications: NotificationInterface[] = [];
    public groupedNotifications: NotificationStatusGroup[] = [];
    public displayedColumns = ['type', 'read', 'label', 'date', 'delete'];
    public displaySearchField = true;
    public selected: NotificationInterface;
    public areReadNotificationsDisplayed = false;
    public readNotificationGroupLimit = 2; // Maximum number of read notification groups to display

    // https://github.com/angular/material2/issues/10205
    private paginator: MatPaginator;
    private sort: MatSort;
    private unsubscribeInTakeUntil = new Subject<void>();

    @ViewChild(MatSort) set matSort(ms: MatSort) {
        this.sort = ms;
        this.setDataSourceAttributes();
    }

    @ViewChild(MatPaginator, {static: true}) set matPaginator(mp: MatPaginator) {
        this.paginator = mp;
        this.setDataSourceAttributes();
    }

    constructor(
        public notificationsService: NotificationsService,
        public notificationsServiceCitizen: NotificationsServiceCitizen,
        private communicationCenter: CommunicationCenterService,
        private router: Router,
    ) {
        this.communicationCenter
            .getRoom('notification')
            .getSubject('display-search-field-notification-list')
            .pipe(
                tap((displaySearchField: boolean) => this.displaySearchField = displaySearchField)
            ).subscribe();
        this.communicationCenter
            .getRoom('notification')
            .getSubject('column-definition-notification-list')
            .pipe(
                tap((columnDef: string[]) => this.displayedColumns = columnDef)
            ).subscribe();

        this.notificationsService.onFilesChanged.pipe(
            takeUntil(this.unsubscribeInTakeUntil))
            .subscribe(notifs => {
                this.notifs = notifs;
            });
        this.notificationsService.onFileSelected.pipe(
            takeUntil(this.unsubscribeInTakeUntil))
            .subscribe(selected => {
                this.selected = selected;
            });

        this.communicationCenter
            .getRoom('notifications')
            .getSubject('myNotifications').pipe(
            map((entities: DataEntity[]) => {
                return entities.filter(entity => {
                    return this.notificationsService.isNotificationTypeRegistered(entity);
                }).map(entity => {

                    const definition: NotificationDefinition = this.notificationsService.getDefinition(entity.get('type'));

                    // Remplacement de Object par Record<string, unknown> pour respecter les règles ESLint
                    const tData: Record<string, unknown> = (typeof entity.get('data') === 'object') ? entity.get('data') : JSON.parse(entity.get('data'));

                    if (definition) {
                        return <NotificationInterface>{
                            notificationType: entity.get('type'),
                            type: definition.icon,
                            read: entity.get('read'),
                            label: tData.formName,
                            data: tData,
                            date: entity.get('created'),
                            id: entity.id,
                            originalEntity: entity,
                            deletable: definition.deletable !== false,
                            action: this.defineAction(entity, tData)
                        };
                    }
                });
            }),
            takeUntil(this.unsubscribeInTakeUntil),)
            .subscribe((notifs: NotificationInterface[]) => {
                this.notifications = notifs;
                this.groupedNotifications = this.groupNotificationsByDate(notifs);
                this.dataSource.data = notifs;
            });
    }

    ngOnInit(): void {
        // If there are no unread notification groups at initialization, display already read notification groups
        if (!this.unreadNotificationsGroup) {
            this.displayReadNotifications();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribeInTakeUntil.next();
        this.unsubscribeInTakeUntil.complete();
    }

    private formatLongDate(timestamp: string | number): string {
        // Convertir en millisecondes si le timestamp est en secondes
        const timestampMs = Number(timestamp) * 1000;
        const date = new Date(timestampMs);

        // Options pour le format long avec jour de la semaine
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };

        // Utiliser la locale française par défaut
        return date.toLocaleDateString('fr-FR', options);
    }

    private groupNotificationsByDate(notifications: NotificationInterface[]): NotificationStatusGroup[] {
        // Séparer les notifications par statut de lecture
        // read peut être false, true, 0, ou 1
        const unreadNotifications = notifications.filter(n => !n.read);
        const readNotifications = notifications.filter(n => !!n.read);

        const result: NotificationStatusGroup[] = [];

        // Grouper les notifications non lues par date
        if (unreadNotifications.length > 0) {
            result.push({
                status: NotificationStatus.toSee,
                dateGroups: this.groupByDate(unreadNotifications)
            });
        }

        // Grouper les notifications lues par date
        if (readNotifications.length > 0) {
            result.push({
                status: NotificationStatus.alreadySeen,
                dateGroups: this.groupByDate(readNotifications)
            });
        }

        return result;
    }

    private groupByDate(notifications: NotificationInterface[]): NotificationGroup[] {
        const groups = new Map<string, NotificationInterface[]>();

        // Grouper les notifications par date
        notifications.forEach(notification => {
            const dateKey = this.formatLongDate(notification.date);
            if (!groups.has(dateKey)) {
                groups.set(dateKey, []);
            }
            groups.get(dateKey).push(notification);
        });

        // Convertir en tableau et trier par date décroissante
        return Array.from(groups.entries())
            .map(([date, notifs]) => ({
                date: date,
                notifications: notifs
            }))
            .sort((a, b) => {
                // Trier les groupes par date décroissante (plus récent en premier)
                const dateA = Number(a.notifications[0]?.date) || 0;
                const dateB = Number(b.notifications[0]?.date) || 0;
                return dateB - dateA;
            });
    }

    public get unreadNotificationsGroup(): NotificationStatusGroup | undefined {
        return this.groupedNotifications.find(group => group.status === NotificationStatus.toSee);
    }

    public get readNotificationsGroup(): NotificationStatusGroup | undefined {
        return this.groupedNotifications.find(group => group.status === NotificationStatus.alreadySeen);
    }

    // Check if there are already read notification groups to display
    public get hasReadNotifications(): boolean {
        return this.groupedNotifications.some(group => group.status === NotificationStatus.alreadySeen);
    }

    // Display already read notification groups
    public displayReadNotifications(): void {
        this.areReadNotificationsDisplayed = true;
    }

    // Return a limited number of already read notification groups to display
    public getLimitedReadNotifications(): NotificationGroup[] {
        const group = this.readNotificationsGroup;
        if (!group) {
            return [];
        }
        return group.dateGroups.slice(0, this.readNotificationGroupLimit);
    }

    // Check if there are more already read notification groups than currently displayed
    public hasMoreReadNotifications(): boolean {
        const group = this.readNotificationsGroup;
        if (!group) {
            return false;
        }
        return group.dateGroups.length > this.readNotificationGroupLimit;
    }

    // Display 2 more already read notification groups
    public displayMoreReadNotifications(): void {
        this.readNotificationGroupLimit += 2;
    }

    defineAction(notificationEntity: DataEntity, dataFromNotificationFormated): NotificationAction {
        if (notificationEntity?.get('type') === 'NEW_ASSIGNATION' || notificationEntity?.get('notificationType') === 'NEW_ASSIGNATION') {
            const assignment = this.notificationsService.assignments.find((assignment) => +assignment.id === +dataFromNotificationFormated.id);
            if (assignment) {
                const lesson = assignment.get('assignated_node');
                return () => {
                    this.notificationsServiceCitizen.markAsRead([notificationEntity]);
                    this.notificationsServiceCitizen.closeNotificationDialog();
                    if (this.notificationsServiceCitizen?.overridePathActionOnNewAssignation() && this.notificationsServiceCitizen?.overridePathActionOnNewAssignation() !== '') {
                        this.router.navigate([this.notificationsServiceCitizen?.overridePathActionOnNewAssignation()]);
                        if (dataFromNotificationFormated?.citizenSubProject && dataFromNotificationFormated?.citizenSubProject !== -1) {
                            setTimeout(() => {
                                this.communicationCenter.getRoom('root-filter').getSubject('selected').next(+dataFromNotificationFormated?.citizenSubProject);
                                this.communicationCenter.getRoom('root-filter').getSubject('selected-force-update').next(+dataFromNotificationFormated?.citizenSubProject);
                            }, 100);
                        }
                    } else {
                        // by default
                        this.router.navigate(['lessons', lesson.id, 'page']);
                    }
                };
            }
        }
        return () => console.log('no action defined');
    }

    private setDataSourceAttributes(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // Datasource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    public onSelect(row: NotificationInterface): void {
        this.notificationsService.onFileSelected.next(row);
        row.read = true;
        this.dataSource.data = [...this.dataSource.data];
        row.originalEntity.set('read', true);
        row.originalEntity.save().subscribe(() => {
            this.notificationsService.refreshMyNotifications();
        });

        if (row.action) {
            row.action();
        }
    }

    public deleteNotification(row: NotificationInterface, evt: MouseEvent): void {
        evt.stopPropagation();
        evt.preventDefault();

        this.notifications = this.notifications.filter(notification => notification.id !== row.id);
        this.groupedNotifications = this.groupNotificationsByDate(this.notifications);
        this.dataSource.data = this.dataSource.data.filter(notification => notification.id !== row.id);
        row.originalEntity.remove().subscribe(() => {
            this.notificationsService.refreshMyNotifications();
        });
    }
}
